import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IIdentity, IUser } from '~/utils/types'

export interface ImpersonationState {
  isImpersonated: boolean
  impersonatorId: string | null
  originalToken: string | null
  originalUser: IUser | null
  originalIdentity: IIdentity | null
  originalPermissions: string[] | null
}

export interface AuthState {
  token: string | null
  user: IUser | null
  permissions: string[] | null
  identity: IIdentity | null
  impersonation: ImpersonationState
}

const initialImpersonation: ImpersonationState = {
  isImpersonated: false,
  impersonatorId: null,
  originalToken: null,
  originalUser: null,
  originalIdentity: null,
  originalPermissions: null,
}

const initialState: AuthState = {
  token: null,
  user: null,
  identity: null,
  permissions: null,
  impersonation: initialImpersonation,
}

export interface StartImpersonationPayload {
  token: string
  user: IUser
  impersonatorId: string
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    changeToken: (state: AuthState, action: PayloadAction<string>) => {
      state.token = action.payload
    },
    changeUser: (state: AuthState, action: PayloadAction<IUser>) => {
      state.user = action.payload
    },
    changeIdentity: (state: AuthState, action: PayloadAction<IIdentity>) => {
      state.identity = action.payload
    },
    changePermissions(state, action: PayloadAction<string[]>) {
      state.permissions = action.payload
    },
    startImpersonation: (state: AuthState, action: PayloadAction<StartImpersonationPayload>) => {
      state.impersonation = {
        isImpersonated: true,
        impersonatorId: action.payload.impersonatorId,
        originalToken: state.token,
        originalUser: state.user,
        originalIdentity: state.identity,
        originalPermissions: state.permissions,
      }
      state.token = action.payload.token
      state.user = action.payload.user
      state.identity = null
      state.permissions = null
    },
    stopImpersonation: (
      state: AuthState,
      action: PayloadAction<{ token: string; refresh_token: string; user: IUser }>,
    ) => {
      state.token = action.payload.token
      state.user = action.payload.user
      state.identity = state.impersonation.originalIdentity
      state.permissions = null
      state.impersonation = initialImpersonation
    },
    logoutUser: (state: AuthState) => {
      state.token = null
      state.user = null
      state.permissions = null
      state.identity = null
      state.impersonation = initialImpersonation
    },
  },
})

export const {
  changeToken,
  changeUser,
  changeIdentity,
  changePermissions,
  startImpersonation,
  stopImpersonation,
  logoutUser,
} = authSlice.actions

export default authSlice.reducer
